<template>
  <div>
    <v-breadcrumbs :items="breadcrumbItems"/>

    <v-card>
      <Header :event="event" focused/>
      <v-card-title class="headline">
        Manage Event
      </v-card-title>

      <div v-if="event">
        <v-alert tile v-if="false && tenant.isDefault" type="success">Get started for free! <!-- See our <router-link class="white--text" to="/pricing">pricing</router-link> for details. --><router-link to="/contact">Contact us</router-link> to discuss how we can help your virtual event or challenge.</v-alert>
        <v-alert tile v-if="!event.published" type="info">This event is not yet published. An event must be published before it can be used.</v-alert>
        <v-alert tile v-if="eventUtil.blockConfigEditing()" type="warning" icon="fa-lock" class="mb-0">This event is live and most settings are locked now. Contact support in case changes are required.</v-alert>
        <v-alert v-if="error || !valid" type="error">{{error || 'There is one or more validation issues. Please check the form fields and correct any errors.' }}</v-alert>
        <v-form ref="form" v-model="valid" lazy-validation>


        <v-expansion-panels v-model="selectedPanel" accordion tile class="mb-4">
        
        <!-- General -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-info-circle</v-icon>
            General Options
          </v-expansion-panel-header>
          <v-expansion-panel-content>

            <p v-if="tenant.isDefault">
              Please read our <a href="https://corporatefitness.helpscoutdocs.com/category/7-creating-and-managing-events" target="_blank">Frequently asked questions</a> on creating and managing events for tips and tricks.
            </p>
            <v-text-field 
              v-model="event.name" 
              :rules="nameRules" 
              label="Event name (required)" 
              hint="The name of your event, preferable with the edition or year included."
              prepend-icon="fa-pencil-alt"
              required>
          </v-text-field>
          <v-text-field 
              v-model="event.email" 
              :rules="emailRules" 
              label="Your contact email (not visible to users) (required)" 
              :hint="`Only used by ${tenant.name} to inform you of product updates and in case we need to contact you. This will not be visible to visitors.`"
              prepend-icon="fa-envelope"
              required>
          </v-text-field>

          <v-text-field 
              v-model="event.url" 
              :rules="urlRules" 
              label="Event website (optional)" 
              prepend-icon="fa-link">
          </v-text-field>
          <v-text-field
            label="Time zone (read only)"
            readonly
            prepend-icon="fa fa-globe"
            v-model="event.timezone"
            >
            <template v-slot:append>
              <v-btn
                v-if="!event.published"
                text
                color="primary"
                class="ma-0"
                @click="openTimezoneDialog">
                {{$t('shared.edit')}}
              </v-btn>
            </template>
            
          </v-text-field>
          <v-row>
            <v-col cols="12" md="6">
              <v-radio-group v-model="event.unit" row class="mt-0" persistent-hint hint="Set distance unit for this event. This overwrites user preferences." prepend-icon="fa-ruler">
                <v-radio label="Metric (kilometers)" value="METRIC"></v-radio>
                <v-radio label="Imperial (miles)" value="IMPERIAL"></v-radio>
              </v-radio-group>
            </v-col>
            <v-col v-if="tenant.id !== 'cofi'" cols="12" md="6">
              <v-switch v-model="event.public" persistent-hint hint="When enabled this event is available for everyone to see, otherwise they may only join when they have the url." prepend-icon="fa-eye" class="mt-0" label="Is public (available for others to see)"></v-switch>
            </v-col>

          </v-row>

            <h3 class="mt-8">Active timeframe</h3>
            <p>Only activities between open and close dates can count for this event. Optionally, you can define sub date ranges per leaderboard and/or badge.</p>
            <v-alert v-if="blockDateEdit" type="warning">The start and end dates cannot be modified anymore as this event has already started. Please reach out to <a href="/contact?mode=support" target="_blank">Support</a> in case you need to change the start or end dates.</v-alert>
            <v-row>
              <v-col cols="12" md="4" class="py-0">
                <DateWithTimeZonePicker v-model="event.from" :disabled="blockDateEdit" :timeZone="event.timezone" label="First date (included)" />
              </v-col>
              <v-col cols="12" md="4" class="py-0">
                <DateWithTimeZonePicker v-model="event.till" :disabled="blockDateEdit" :timeZone="event.timezone" label="Last date (included)" />
              </v-col>
              <v-col v-if="tenant.id !== 'cofi'" cols="12" md="4" class="py-0">
                <v-switch v-model="event.ignore_timezone" class="mt-0" label="Global rolling start/finish" hint="Enable to ignore the time zone from activities and allow rolling start/finish on the challenge based on the (local) date." persistent-hint></v-switch>
              </v-col>
              <v-col cols="12" class="py-0">
                <DateAndTimeWithTimeZonePicker v-model="event.countdown" :timeZone="event.timezone" label="Countdown time (leave blank to use the start date)" />
              </v-col>
              <v-col v-if="event.type === 'HYBRID' || event.type === 'RESULTS'" cols="12" class="py-0">
                <DateWithTimeZonePicker v-model="event.display_date" :timeZone="event.timezone" label="Display Date (leave blank to use the start date)" />
              </v-col>
            </v-row>

          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Onboarding -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-handshake</v-icon>
            Onboarding
          </v-expansion-panel-header>
          <v-expansion-panel-content>

            <v-row>

              <v-col cols="12" md="12" class="pb-0">
                  <h3>Invite Method</h3>
                  <v-input class="mb-2 input-row" label="Select your invite method" hide-details>
                    <template>
                      <v-btn-toggle v-model="calculatedVerificationMethod" mandatory class="multiline-btns">
                        <v-btn v-if="tenant.id!=='cofi'" :disabled="eventUtil.blockConfigEditing()" value="_" small>
                          None
                        </v-btn>
                        <v-btn :disabled="eventUtil.blockConfigEditing()" value="ALLOWLIST" small>
                          Re-usable<br/>QR+Link
                        </v-btn>
                        <v-btn :disabled="eventUtil.blockConfigEditing()" value="REG_DATA" small>
                          Personal<br/>Invites
                        </v-btn>
                      </v-btn-toggle>
                    </template>
                  </v-input>
                  <InlineTip v-if="!event.verification_method" class="mb-4">
                    No verification code is required to join. Everyone who has the link can join.
                  </InlineTip>
                  <InlineTip v-if="event.verification_method === 'ALLOWLIST'" class="mb-4">
                    Share a generic QR code/link to invite the participants. Anyone with this (reusable) QR code or link may join.
                  </InlineTip>
                  <InlineTip v-if="event.verification_method === 'REG_DATA'" class="mb-4">
                    Upload an Excel file to control who may join this event or manually manage registration data. Each person in the file receives a personal invite email to join this event.
                  </InlineTip>

                  <!--
                    <router-link v-if="event.verification_method === 'ALLOWLIST'" class="ml-8" :to="{name:'eventmanagerAllowlist', params: {id: event.id}}">Manage verification codes</router-link>
                    <router-link v-if="event.verification_method === 'REG_DATA'" class="ml-8" :to="{name:'eventmanagerRegistrations', params: {id: event.id}}">Upload registration data</router-link>
                  -->
                  <div v-if="event.verification_method">
                    <h3>Customize the Join Event options</h3>
                    <v-switch v-model="askForConsent" :disabled="eventUtil.blockConfigEditing() || !eventUtil.isProPlan()" class="mt-0" label="Ask for explicit consent when joining" hint="Enable to require all participants to give explicit consent before joining this event. PRO or higher only." persistent-hint></v-switch>
                    <v-textarea
                      v-if="askForConsent || event.join_consent_msg"
                      :disabled="eventUtil.blockConfigEditing()"
                      v-model="event.join_consent_msg" 
                      class="mt-0 ml-12 mb-4"
                      label="Consent message (to be accepted by participants before joining)"
                      persistent-hint
                      hint="When entered every participant must check a box to consent with above message. Use this to require explicit consent of participants for your challenge."
                      rows="1"
                      auto-grow
                      />

                    <v-switch 
                      v-if="event.verification_method != 'REG_DATA'" 
                      :disabled="eventUtil.blockConfigEditing()"
                      v-model="event.join_force_name" 
                      class="mt-0" 
                      label="Require profile name entry" 
                      hint="Enable to require all participants to enter a profile name when they join." 
                      persistent-hint />
                    <v-switch 
                      v-if="event.verification_method != 'REG_DATA' && event.join_force_name" 
                      :disabled="eventUtil.blockConfigEditing()"
                      v-model="event.name_check_algo" 
                      class="ms-12 mt-0" 
                      label="Enforce real-name" 
                      hint="Enable to require users to enter their full name and block (most) nicknames." 
                      persistent-hint 
                      :false-value="null" 
                      true-value="relaxed"
                      />
                    <v-switch 
                      v-if="event.verification_method != 'REG_DATA'" 
                      :disabled="eventUtil.blockConfigEditing()"
                      v-model="event.join_require_email" 
                      class="mt-0" 
                      label="Require email address" 
                      hint="Enable to require all participants to enter their email address when they join." 
                      persistent-hint
                      />

                    <v-alert v-if="!eventUtil.isEnterprisePlan()" type="info" class="mt-12">
                      <div class="d-flex flex-row align-content-center">
                        <p class="mb-0">Advanced onboarding configuration options are available in the Enterprise plans only.</p>
                        <v-btn class="flex-align-right white--text" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">Upgrade</v-btn>
                      </div>
                    </v-alert>

                    <v-switch 
                      v-if="event.join_select_team" 
                      :disabled="eventUtil.blockConfigEditing() || !eventUtil.isEnterprisePlan()" 
                      v-model="event.join_use_team_as_race" 
                      class="mt-0" 
                      label="Join leaderboards based on team" 
                      hint="Enable to let participants join leaderboards based on selected team." 
                      persistent-hint
                      />
                    <!-- <v-switch :disabled="!eventUtil.isProPlan()" v-model="askForRaces" class="mt-0" label="Ask for leaderboards to join" hint="Enable to require participants to select their leaderboard preference." persistent-hint></v-switch> -->
                    <v-combobox
                      v-if="event.join_select_race_import_ids"
                      v-model="event.join_select_race_import_ids"
                      label="Leaderboard selection options"
                      persistent-hint
                      hint="Enter one or more options, each followed by the TAB key. These should be mapped to the value of the 'Join Key' field on one or more leaderboard(s)."
                      class="mt-0 ml-12 mb-4"
                      multiple
                      chips
                    ></v-combobox>
                    <MarkdownEditor 
                      v-if="askForRaces || event.join_select_race_import_ids" 
                      :disabled="eventUtil.blockConfigEditing()"
                      v-model="event.join_race_msg" 
                      :eventId="event.id" 
                      class="ml-12" 
                      label="Customized Leaderboard Selection Explanation" 
                      />

                    <v-text-field
                      v-if="false && !event.verification_method"
                      v-model="event.join_gender_msg" 
                      class="mt-0"
                      label="Request gender message on join message"
                      persistent-hint
                      hint="When entered, every participant must select their gender when joining a challenge. Leave empty unless absolutely required by your event."
                      >
                    </v-text-field>
                    <div>
                    <RegFieldEditor :disabled="eventUtil.blockConfigEditing() || !eventUtil.isEnterprisePlan()" v-model="event.join_udf_1_field" name="Custom Question 1" />
                    <RegFieldEditor :disabled="eventUtil.blockConfigEditing() || !eventUtil.isEnterprisePlan()" v-model="event.join_udf_2_field" name="Custom Question 2" />
                    <RegFieldEditor :disabled="eventUtil.blockConfigEditing() || !eventUtil.isEnterprisePlan()" v-model="event.join_udf_3_field" name="Custom Question 3" />
                    </div>
                  </div>
              </v-col>
            </v-row>

          </v-expansion-panel-content>
        </v-expansion-panel>
        

        <!-- Onboarding -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-users</v-icon>
            Teams
          </v-expansion-panel-header>
          <v-expansion-panel-content>

            <h3 class="mt-4">
              Team Mode
              <v-btn text color="primary" href="https://corporatefitness.helpscoutdocs.com/article/80-creating-team-leaderboards" target="_blank">Learn More</v-btn>
            </h3>
            <v-radio-group :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" v-model="calculatedTeamMode">
              <v-radio value="NONE" class="mb-4">
                <template v-slot:label>
                  <div>
                    <div><strong>No Teams</strong></div>
                    <div>Team leaderboards will not be available for this event.</div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="SELF_MANAGED" class="mb-4">
                <template v-slot:label>
                  <div>
                    <div><strong>Chosen by participant</strong></div>
                    <div>Participants can choose their team preference when they join.</div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="RANDOM" class="mb-4">
                <template v-slot:label>
                  <div>
                    <div><strong>Automatically assigned</strong></div>
                    <div>Participants are automatically and randomly assigned into teams when they join.</div>
                  </div>
                </template>
              </v-radio>
              <v-radio value="ADMIN">
                <template v-slot:label>
                  <div>
                    <div><strong>Assigned by admin</strong></div>
                    <div>Event admins control the team assignment.</div>
                  </div>
                </template>
              </v-radio>
            </v-radio-group>    
           
            <v-sheet v-if="calculatedTeamMode !== 'NONE'" class="admin-input-container ps-8">
              <h4>Team mode configuration</h4>
              <InlineTip v-if="event.admin_assigns_team" href="https://corporatefitness.helpscoutdocs.com/article/80-creating-team-leaderboards">
                Event admins control the team assignment. Read the docs for an overview of the various tools available to you.
              </InlineTip>
              
              <v-radio-group v-if="event.join_select_team" :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" v-model="event.join_block_team_create">
                <v-radio :value="false" label="Participants have the option to create a new team or join an existing team"/>
                <v-radio :value="true" label="Participants must select from a list of pre-defined teams and cannot create a new team"/>
              </v-radio-group>
              <v-switch v-if="event.join_select_team" :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" v-model="event.join_force_team" class="mt-4" label="Force participants to select a team option" hint="Enable to prevent participants from joining without selecting a team option." persistent-hint></v-switch>
              <MarkdownEditor v-if="event.join_select_team" :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" v-model="event.join_team_msg" :eventId="event.id" class="mt-4" label="Enter instructions you would like participants to know about team selection here (optional)" />
              
              <v-radio-group v-if="event.join_random_team_assignment" :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" v-model="dynamicallyCreateTeams" @change="event.team_name_algo=null;event.team_max_members=null;">
                <v-radio :value="false" label="Assign from list of pre-defined teams"/>
                <v-radio :value="true" label="Dynamically create teams as participants join "/>
              </v-radio-group>
              <v-select
                v-if="event.join_random_team_assignment && dynamicallyCreateTeams"
                :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" 
                v-model="event.team_name_algo"
                class="mt-4"
                label="How would you want to name the teams?"
                item-text="text"
                item-value="type"
                :items="$helpers.prefixSelectListWith(siteData.team_naming_algos, '- custom team names -')"
                />

              <v-row v-if="(event.join_random_team_assignment && dynamicallyCreateTeams) || (event.join_select_team)">
                <v-col cols="12" class="pb-0">
                  <h3>Team Size</h3>
                  <p class="mb-0">Use these options to limit the number of members in each team.</p>
                </v-col>

                <v-col v-if="computedMinTeamSize || eventUtil.isEnterprisePlan()" cols="12" md="6" class="pt-0">
                  <v-text-field 
                      :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" 
                      v-model.number="computedMinTeamSize" 
                      type="number"
                      min="0" 
                      step="1" 
                      label="Min Team Size" 
                      persistent-hint
                      hint="Leave empty for an unrestricted team size.">
                  </v-text-field>
                  <v-alert v-if="event.team_min_members != null" type="info" dense>
                    Teams with fewer members will show on the leaderboard with a score of 0, until they reach the minimum size.
                  </v-alert>
                </v-col>
                <v-col cols="12" md="6" class="pt-0">
                  <v-text-field 
                      :disabled="eventUtil.blockConfigEditing() || eventUtil.hideAdvancedEventOptions()" 
                      v-model.number="computedMaxTeamSize" 
                      type="number"
                      min="0" 
                      step="1" 
                      label="Max Team Size" 
                      persistent-hint
                      :hint="event.join_random_team_assignment ? '' : 'Leave empty for an unrestricted team size.'">
                  </v-text-field>
                  <v-alert v-if="event.team_max_members != null && event.team_max_members < 2" type="info" dense>
                    Please note that a value lower than 2 is not advised. This will not allow teams to be joined.
                  </v-alert>
                  <v-alert v-if="event.team_min_members != null && event.team_max_members != null && event.team_max_members < event.team_min_members" type="warning" dense>
                    Please select a maximum team size larger than the minimum team size.
                  </v-alert>
                </v-col>
              </v-row>
            </v-sheet>

          </v-expansion-panel-content>
        </v-expansion-panel>
        
        
        <!-- Rules -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-cogs</v-icon>
            Rules
          </v-expansion-panel-header>
          <v-expansion-panel-content>

            <v-row>

              <v-col cols="12" class="pb-0">
                <h3>Entry and validation</h3>
                <p class="mb-0">Use these options to customize the general event experience.</p>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch 
                :disabled="eventUtil.blockConfigEditing()" 
                v-model="event.allow_manual_entry" 
                persistent-hint 
                hint="When enabled, manually entered activities (without gpx track nor proof) may qualify for this event." 
                prepend-icon="fa-pencil-alt" 
                class="mt-0" 
                label="Allow manual activity entry"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-switch 
                :disabled="eventUtil.blockConfigEditing()" 
                v-model="event.validate_activities" 
                persistent-hint 
                hint="When enabled, manual and auto-synced entries will be validated based on their activity type, distance and speed." 
                prepend-icon="fa-check-double" 
                class="mt-0" 
                label="Validate speed/pace"
                />
              </v-col>

              <v-col cols="12" class="pb-0">
                <h4>Step Challenges</h4>
                <p class="mb-0">Use these options to fine-tune the rules for step challenges.</p>
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field 
                  :disabled="eventUtil.blockConfigEditing()" 
                  v-model.number="computedMaxDailyStepsSize" 
                  type="number"
                  min="0" 
                  step="1" 
                  label="Max Daily Steps" 
                  persistent-hint
                  hint="Leave empty for an unrestricted daily steps."
                  />
                <v-alert v-if="event.daily_steps_max != null" type="info" outlined>
                  Please note that this is a <i>per daily/activity</i> maximum. When conversions are allowed, a participant <i>can</i> therefor exceed this limit by having multiple activities being converted to steps on the same day.
                </v-alert>
                <v-alert v-if="event.daily_steps_max != null && event.daily_steps_max < 10000" type="info" dense>
                  We don't allow setting this to a value below 10,000 steps per day.
                </v-alert>
              </v-col>
              <v-col v-if="event.allow_manual_entry" cols="12" md="6">
                <v-text-field 
                  :disabled="eventUtil.blockConfigEditing()" 
                  v-model.number="computedMaxDailyStepsManualEntry" 
                  type="number"
                  min="0" 
                  step="1" 
                  label="Max steps for manual entry" 
                  persistent-hint
                  hint="Leave empty for an unrestricted daily step entry."
                  />
                <v-alert v-if="event.daily_steps_max_manual != null" type="info" outlined>
                  Please note that this is a <i>per entry</i> maximum. This is used to discourage (not prevent!) users to enter large number manually. Use the Max Per Day for an absolute daily maximum.
                </v-alert>
              </v-col>
              <v-col cols="12" md="6">
                <v-switch 
                  :disabled="eventUtil.blockConfigEditing() || !eventUtil.isProPlan()" 
                  v-model="event.auto_manual_entry_step_conversion" 
                  label="Auto-convert activities to steps" 
                  persistent-hint 
                  hint="When enabled, all activities are automatically converted to steps based on the device and type of activity." 
                  prepend-icon="fa-calculator" 
                  class="mt-0"
                  />
                <a class="ml-8 pt-4" href="https://corporatefitness.helpscoutdocs.com/article/105-step-conversion">More on Step Conversion</a>
                
                <v-alert v-if="!eventUtil.isProPlan()" type="info" tile outlined>
                  <div class="d-flex flex-row align-content-center">
                    <p class="mb-0">Conversions require the Pro or Enterprise plan.</p>
                    <v-btn class="flex-align-right" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">Upgrade</v-btn>
                  </div>
                </v-alert>
              </v-col>
            </v-row>
            <v-row v-if="event.activity_mode == 'ACTIVITY_BASED' || event.activity_mode == 'MIXED'">
              <v-col cols="12" class="pb-0">
                <h4>Activity-based Challenges</h4>
                <p class="mb-0">Use these options to fine-tune the rules for activity-based events. The rules are applied per activity. Admins can always manually approve rejected activities.</p>
              </v-col>
              <v-col cols="12" md="6">
                <DistanceTextArea
                  :disabled="eventUtil.blockConfigEditing()" 
                  v-model="event.daily_duration_max" 
                  :mode="$helpers.UnitType.NUMBER"
                  :multiplier="3600"
                  label="Max Daily Duration (in hours, overall)" 
                  hint="Leave empty to allow all."
                  />
              </v-col>
              <v-col cols="12" md="6">
                <DistanceTextArea
                  :disabled="eventUtil.blockConfigEditing()" 
                  v-model="event.activity_duration_max" 
                  :mode="$helpers.UnitType.NUMBER"
                  :multiplier="3600"
                  label="Max Activity Duration (in hours)" 
                  hint="Leave empty to allow all."
                  />
              </v-col>
              <v-col v-if="event.allow_manual_entry && event.activity_duration_max" cols="12" md="6">
                <DistanceTextArea
                  :disabled="eventUtil.blockConfigEditing()" 
                  v-model="event.activity_duration_max_manual" 
                  :mode="$helpers.UnitType.NUMBER"
                  :multiplier="3600"
                  label="Max Activity Duration (for manual entry, in hours)" 
                  hint="Leave empty to use default."
                  />
              <v-alert v-if="event.activity_duration_max_manual_entry >= event.activity_duration_max" type="warning" outlined>
                Please note that the max per manual entry should be lower than the overall maximum.
              </v-alert>
              </v-col>
            </v-row>
            <v-row>

              <template v-if="showMinMaxRules">

                <v-col cols="12" class="pb-0">
                  <h3>Personal Fitness Goals</h3>
                  <p class="mb-0">Use these options to restrict daily fitness goals. You can leave fields empty to allow any value the participant enters or chose to restrict only lower or upper boundaries.</p>
                </v-col>

                <v-col cols="12" md="6" class="pt-0">
                  <DistanceTextArea
                    :disabled="eventUtil.blockConfigEditing()" 
                    v-model="event.step_goal_min" 
                    :mode="$helpers.UnitType.NUMBER"
                    label="Daily Step Goal Minimum" 
                    hint="Leave empty to remove this limit."
                    />
                </v-col>

                <v-col cols="12" md="6" class="pt-0">
                  <DistanceTextArea
                    :disabled="eventUtil.blockConfigEditing()" 
                    v-model="event.step_goal_max" 
                    :mode="$helpers.UnitType.NUMBER"
                    label="Daily Step Goal Maximum" 
                    hint="Leave empty to remove this limit."
                    />
                </v-col>

                <v-col cols="12" md="6" class="pt-0">
                  <DistanceTextArea
                    :disabled="eventUtil.blockConfigEditing()" 
                    v-model="event.time_goal_min" 
                    :mode="$helpers.UnitType.NUMBER"
                    :multiplier="60"
                    label="Daily Active Time Goal Minimum" 
                    hint="In minutes. Leave empty to remove this limit."
                    />
                </v-col>

                <v-col cols="12" md="6" class="pt-0">
                  <DistanceTextArea
                    :disabled="eventUtil.blockConfigEditing()" 
                    v-model="event.time_goal_max" 
                    :mode="$helpers.UnitType.NUMBER"
                    :multiplier="60"
                    label="Daily Active Time Goal Maximum" 
                    hint="In minutes. Leave empty to remove this limit."
                    />
                </v-col>

                <v-col cols="12" md="6" class="pt-0">
                  <DistanceTextArea
                    :disabled="eventUtil.blockConfigEditing()" 
                    v-model="event.kcal_goal_min" 
                    :mode="$helpers.UnitType.NUMBER"
                    label="Daily Calorie Goal Minimum" 
                    hint="In kcal. Leave empty to remove this limit."
                    />
                </v-col>

                <v-col cols="12" md="6" class="pt-0">
                  <DistanceTextArea
                    :disabled="eventUtil.blockConfigEditing()" 
                    v-model="event.kcal_goal_max" 
                    :mode="$helpers.UnitType.NUMBER"
                    label="Daily Calorie Goal Maximum" 
                    hint="In kcal. Leave empty to remove this limit."
                    />
                </v-col>
              </template>

            </v-row>
            <v-row v-if="eventUtil.hasPaidSupportPlan()">
              <v-col cols="12" class="pb-0">
                <h3>Back loading</h3>
                <p class="mb-0">Use these options to fine-tune the back loading options. Please user these options with care as they might result in additional support questions by users.</p>
              </v-col>

              <v-col cols="12" md="6" class="pt-0">
                <v-text-field 
                  :disabled="eventUtil.blockConfigEditing()" 
                  v-model.number="computedAllowedHistoryDays" 
                  type="number"
                  min="0" 
                  step="1" 
                  label="Allowed days of activity history when joining" 
                  persistent-hint
                  hint="This is relative to the users' join timestamp of the event. Use this option to prevent users to join an event and immediately load past activity data. Set to a reasonable amount or enter 0 to prevent all activities from before join timestamp altogether."
                  />
                <v-alert v-if="event.allowed_history_days != null && event.allowed_history_days < 3" type="info" dense>
                  Please note that a value lower than 3 is not advised. Setting back loading to a short time will result in data not counting towards the event and higher support load.
                </v-alert>
              </v-col>
              <v-col cols="12" md="6" class="pt-0">
                <v-text-field 
                  :disabled="eventUtil.blockConfigEditing()"                 
                  v-model.number="computedAllowedBackLoadingDays" 
                  type="number"
                  min="0" 
                  step="1" 
                  label="Allowed days to enter/sync activities" 
                  persistent-hint
                  hint="Enter the number of days you want users to be able to enter/sync activities after they were started. Use this option to prevent users to enter their activities long after they happened."
                  />
                <v-alert v-if="event.allowed_backloading_days != null && event.allowed_backloading_days < 3" type="info" dense>
                  Please note that a value lower than 3 is not advised. Setting back loading to a short time will result in data not counting towards the event and higher support load.
                </v-alert>
              </v-col>
            </v-row>
            <v-row>

              <v-col v-if="tenant.id !== 'cofi'" cols="12" md="6" class="pt-0">
                <h3>Participation Duration</h3>
                <v-text-field 
                  :disabled="eventUtil.blockConfigEditing()" 
                  v-model.number="computedMaxParticipationDuration" 
                  type="number"
                  min="0" 
                  step="1" 
                  label="Max allowed days to complete" 
                  persistent-hint
                  hint="Enter the maximum number of days you want users to have to complete the event."
                  />
                <v-alert v-if="event.max_participation_days != null && event.max_participation_days < 7" type="info" dense>
                  Please note that a value lower than 7 is not advised. This value is always within the event validity period.
                </v-alert>
              </v-col>

              <v-col v-if="tenant.id !== 'cofi'" cols="12" md="12" class="pb-0">
                <h3>Allowed wearable integrations</h3>
                <v-select
                  :disabled="eventUtil.blockConfigEditing()"                 
                  :items="siteData.providers"
                  v-model="event.allowed_providers" 
                  multiple
                  item-value="type"
                  item-text="text"
                  persistent-hint
                  hint="Leave 'All Wearables' selected unless you explicitly want to configure this."
                  label="Allowed Wearables"
                >
                  <template v-slot:prepend-item>
                    <v-list-item @click="checkAllProviders" :class="allProvidersSelected ? 'v-list-item--active':''">
                      <v-list-item-action>
                        <v-icon color="primary">{{ allProvidersSelected ? 'fa-check-square' : 'fal fa-square' }}</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>All Wearables</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-select>                  
              </v-col>
            </v-row>

          </v-expansion-panel-content>
        </v-expansion-panel>
           

        <!-- Lifecycle -->
        <v-expansion-panel v-if="eventUtil.showAdvancedEventOptions()">
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-calendar-week</v-icon>
            Lifecycle
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p>Control the lifecycle of this event.</p>
            <v-row>
              <v-col cols="12" md="4" class="py-0">
                <DateWithTimeZonePicker v-model="event.join_till" :timeZone="event.timezone" :minDate="event.from" :maxDate="event.till" label="Join deadline (included)" hint="Last day people can still join this event. Leave empty to keep open until event ends." />
              </v-col>
            </v-row>
            <v-row v-if="tenant.id == 'wmm'">
              <v-col cols="12" class="py-0 mt-4">
                <h4>Show as Featured</h4>
                <p>Configure to show this event as featured between the selected dates.</p>
              </v-col>
              <v-col cols="12" md="4" class="py-0">
                <DateWithTimeZonePicker v-model="event.feature_from" :timeZone="event.timezone" label="Feature From (included)" hint="First day this event shows as featured." />
              </v-col>
              <v-col cols="12" md="4" class="py-0">
                <DateWithTimeZonePicker v-model="event.feature_till" :timeZone="event.timezone" label="Feature Until (included)" hint="Last day this event shows as featured (included)." />
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" class="py-0 mt-4">
                <h4>Result Locking</h4>
                <v-alert v-if="!eventUtil.isProPlan()" type="info" class="">
                  <div class="d-flex flex-row align-content-center">
                    <p class="mb-0">Locking results is available in the Pro and Enterprise plans only.</p>
                    <v-btn class="flex-align-right white--text" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">Upgrade</v-btn>
                  </div>
                </v-alert>
                <v-alert v-if="event.auto_locked" type="info">This event is currently locked due to its current configuration.</v-alert>
                <p>Configure when you would like leaderboards to stop accepting changes.</p>
              </v-col>
              <v-col cols="12" md="4" class="py-0">
                <div>Relative date (advised):</div>
                <v-text-field 
                    :disabled="!eventUtil.isProPlan()"
                    v-model.number="computedAutoLockDays" 
                    type="number"
                    min="0" 
                    step="1" 
                    label="Days after end of event." 
                    persistent-hint
                    hint="Auto lock so many days after the event has ended."
                    />
              </v-col>
              <v-col cols="12" md="8" class="py-0">
                <div>Specific date and time:</div>
                <DateAndTimeWithTimeZonePicker :disabled="!eventUtil.isProPlan()" v-model="event.auto_lock_on" :timeZone="event.timezone" label="Auto-lock date" hint="Select the timestamp when you want the leaderboards to be locked." />
              </v-col>
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        



        <!-- Customization -->
        <v-expansion-panel v-if="eventUtil.showAdvancedEventOptions()">
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-edit</v-icon>
            Customization
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <div v-if="event.type === 'STEPS'">
              <v-radio-group v-model="event.result_format" label="Result Display Mode">
                <v-radio :value="null" class="mb-4">
                  <template v-slot:label>
                    <div>
                      <div><strong>In Steps</strong></div>
                      <div>All results, including converted activities will show as 'steps'.</div>
                    </div>
                  </template>
                </v-radio>
                <v-radio value="POINTS" class="mb-4">
                  <template v-slot:label>
                    <div>
                      <div><strong>In Points</strong></div>
                      <div>All results will show in 'points'. 1 step equals 1 point. Converted activities also show in points.</div>
                    </div>
                  </template>
                </v-radio>
              </v-radio-group>

            </div>

            <v-alert v-if="!eventUtil.isProPlan()" type="info" class="">
              <div class="d-flex flex-row align-content-center">
                <p class="mb-0">These customization options are available in the Pro and Enterprise plans only.</p>
                <v-btn class="flex-align-right white--text" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">Upgrade</v-btn>
              </div>
            </v-alert>
            <v-row>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Customize the event and app content</h3>
                  <MarkdownEditor :disabled="!eventUtil.isProPlan()" v-model="event.intro" :eventId="event.id" label="Intro (on top of event page)" />
                  <MarkdownEditor :disabled="!eventUtil.isProPlan()" v-model="event.description" :eventId="event.id" label="Description (at bottom of event page)" />

                  <h3>Getting Started Instructions</h3>
                  <p class="text-muted">Enter instructions to help participants to get started with your event. These instructions are displayed on the profile page until a result has been created.</p>
                  <p class="mb-0 text-muted"><v-icon small color="grey" class="me-1">fa-lock</v-icon> This information is only available to participants of this event.</p>
                  <MarkdownEditor :disabled="!eventUtil.isProPlan()" v-model="event.instructions" :eventId="event.id" label="Getting Started Instructions" />
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Custom Rules Explanation</h3>
                  <p class="text-muted">Enter a custom rules description to replace the default auto-generated rules section on the event page. These instructions are visible on the event home page.</p>
                  <MarkdownEditor :disabled="!eventUtil.isProPlan()" v-model="event.rules_desc" :eventId="event.id" label="Customized Rules Explanation" />
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Custom Onboarding Message</h3>
                  <p class="text-muted">Enter a custom onboarding message. This message is displayed when people start onboarding.</p>
                  <MarkdownEditor :disabled="!eventUtil.isProPlan()" v-model="event.onboarding_msg" :eventId="event.id" label="Customized Onboarding Message" />
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Custom Join Message</h3>
                  <p class="text-muted">Enter a custom join message. This message is displayed on the join screen where people can select their team and/or category.</p>
                  <MarkdownEditor :disabled="!eventUtil.isProPlan()" v-model="event.join_msg" :eventId="event.id" label="Customized Join Message" />
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Team Invite Share Message</h3>
                  <p class="text-muted">Enter a custom message. This message is displayed when a team member wants to share a join link.</p>
                  <MarkdownEditor :disabled="!eventUtil.isProPlan()" v-model="event.share_team_invite_msg" :eventId="event.id" label="Customized Team Invite Message" />
              </v-col>
              <v-col cols="12" md="12" class="pb-0">
                  <h3>Pinned Leaderboard</h3>
                  <p class="text-muted">You can pin a specific leaderboard, or part of its data to the top of the leaderboard list in the app, website, and on Microsoft Teams.</p>

                  <v-select
                    :disabled="!eventUtil.isProPlan()"
                    :items="event.races"
                    v-model="event.pinned_race_id" 
                    item-value="id"
                    item-text="name"
                    label="Pinned Leaderboard"
                    persistent-hint
                    hint="Select the leaderboard you want to pin at the top."
                    class="mt-8"
                    />

                  <v-select
                    v-if="event.pinned_race_id"
                    :items="siteData.race_data_views"
                    v-model="event.pinned_views" 
                    multiple
                    item-value="value"
                    item-text="text"
                    label="Pinned Views"
                    persistent-hint
                    hint="Select what data you want to see."
                    class="mt-8"
                    />
              </v-col>

            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        





        <!-- Emails -->
        <v-expansion-panel v-if="tenant.id !== 'cofi' && tenant.id !== 'wmm'">
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-envelope</v-icon>
            Emails
          </v-expansion-panel-header>
          <v-expansion-panel-content>
              <v-alert type="info" outlined>All email customization options will soon move to the new <router-link :to="{name:'eventmanagerEmails', params: {id:event.id}}">Email Customization</router-link> screen.</v-alert>
              <h3>Invite Email</h3>
              <p>Enable this option to trigger an automatic invite email after importing new registration records. The invite emails will be send out automatically to newly created registration records when uploading registration data. Leave empty to use default template. Click 'Send test email' to review the template.</p>
              <p class="">Important note: always test on your mobile and not a laptop/desktop as most people will read their invite on their mobile phone. Layout options are deliberately kept simple to optimize email delivery.</p>
              <v-btn v-if="event.send_invite_email" outlined color="secondary" class="float-right" @click="sendTestEmail">Send test email</v-btn>
              <v-switch v-model="event.send_invite_email" class="mt-0" label="Send email on import" hint="Enable to send a (customizable) email whenever a new registration record is imported." persistent-hint></v-switch>
              <v-alert v-if="originalEvent && !originalEvent.send_invite_email && event.send_invite_email" type="info">Note: all current registrations will be marked as invited to prevent accidentally mass-mailing them during the next import.</v-alert>
              <MarkdownEditor v-if="event.send_invite_email" v-model="event.invite_email_text" :eventId="event.id" label="Email text" />

              <h3 class="mt-8">Join Confirmation Email Template</h3>
              <p>When someone joins this event, they will receive an email with key information required to login again. This helps people to remember how they signed up and re-connect to the event from another device.</p>
              <MarkdownEditor v-model="event.join_confirmation_email_template" :eventId="event.id" label="Invite Email" />

              <h3 class="mt-8">Bib Email</h3>
              <p>Enable this option to trigger an automatic email when a virtual bib is generated. The emails will be send out automatically to newly created registration records when uploading registration data.</p>
              <v-btn v-if="event.send_bib_email" outlined color="secondary" class="float-right" @click="sendTestBibEmail">Send test email</v-btn>
              <v-switch v-model="event.send_bib_email" class="mt-0" label="Send email" hint="Enable to send a (customizable) email whenever a new bib is generated." persistent-hint></v-switch>
              <v-textarea
                v-if="event.send_bib_email"
                v-model="event.bib_email_text" 
                class="mt-0"
                label="Email text"
                placeholder="Leave empty to use default template. Click 'Send test email' to review the template."
                persistent-hint
                hint="Optional text to include in emails. Leave empty to use the default template. HTML content is allowed."
                >
              </v-textarea>
          </v-expansion-panel-content>
        </v-expansion-panel>




        <!-- Refer a Friend -->
        <v-expansion-panel v-if="tenant.id !== 'cofi'">
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-bullhorn</v-icon>
            Refer a Friend
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            
            <p>Encourage your participants to invite their friends to register for this event using our Refer-a-Friend tool.</p>

            <v-text-field
              v-model="event.referral_msg" 
              class="mt-0 mb-8"
              label="Refer-a-friend message"
              persistent-hint
              hint="Promotional message to encourage participants to invite their friends. Should be directed at the participant and not the invitee."
              />
                        
            <h4>Refer-a-Friend Email Template</h4>
            <v-btn outlined color="secondary" class="float-right" @click="sendTestFriendInviteEmail">Send test email</v-btn>
            <p>This is the default template for refer-a-friend emails. This may be overwritten on race level to provide more specific instructions.</p>
            <v-textarea
              v-model="event.referral_email_template" 
              :rows="event.referral_email_template ? 6 : 2"
              class="mt-0"
              label="Email text"
              placeholder="Leave empty to use default template. Click 'Send test email' to review the template."
              persistent-hint
              hint="Optional text to include in emails. Should be directed at invitee. Leave empty to use the default template. HTML content is allowed."
              >
            </v-textarea>
          

          </v-expansion-panel-content>
        </v-expansion-panel>
        
        
        <!-- Results locking -->
        <v-expansion-panel>
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-life-ring</v-icon>
            User Assistance <v-spacer/> <span> <v-chip v-if="event.support_url || event.support_email" color="primary" small>Configured</v-chip></span> 
          </v-expansion-panel-header>
          <v-expansion-panel-content>

            <p>Allow users to quickly reach out in case they have a question during onboarding or while participating in this event.</p>
            <v-row class="mb-4"> 
            <v-col cols="12" md="6" class="py-0">
              <v-text-field 
                  v-model="event.support_url" 
                  :rules="urlRules" 
                  label="Support website" 
                  persistent-hint
                  hint="How can participants reach you when they have questions?"
                  prepend-icon="fa-life-ring">
              </v-text-field>
            </v-col>
            <v-col cols="12" md="6" class="py-0">
              <v-text-field 
                  v-model="event.support_email" 
                  :rules="optionalEmailRules" 
                  label="Support email" 
                  persistent-hint
                  hint="How can participants reach you when they have questions?"
                  prepend-icon="fa-envelope-open-text"
                  >
              </v-text-field>
            </v-col>
            </v-row>

            <h3>Custom Support Messages</h3>
            <p>Customize the message displayed to users when they request assistance.</p>
            <p class="text-muted">Assistance message during onboarding. This message is displayed during the onboarding while users are connecting with this event.</p>
            <MarkdownEditor v-model="event.support_onboarding_message" :eventId="event.id" label="Customized Support Description (onboarding)" />
            <p class="text-muted">Assistance message for connected participants. This message is displayed to users who are connected with this event and request assitance.</p>
            <MarkdownEditor v-model="event.support_connected_message" :eventId="event.id" label="Customized Support Description (connected)" />

          </v-expansion-panel-content>
        </v-expansion-panel>
        
        
        <!-- Security + Privacy -->
        <v-expansion-panel v-if="eventUtil.showAdvancedEventOptions()">
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-shield</v-icon>
            Security &amp; Privacy
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <v-alert v-if="!eventUtil.isEnterprisePlan()" type="info" class="">
              <div class="d-flex flex-row align-content-center">
                <p class="mb-0">Most of these settings are available in the Enterprise plans only.</p>
                <v-btn class="flex-align-right white--text" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">Upgrade</v-btn>
              </div>
            </v-alert>

            <p>Configure data security and privacy related settings of this event.</p>

              <v-alert v-if="!event.sso_enabled" type="info" outlined>
                SSO is not yet enabled.
                <v-spacer />
                <v-btn color="info" outlined :to="{name:'eventmanagerIntegrationsSso', params: {id: event.id}}">Configure</v-btn>
              </v-alert>
              <v-alert v-if="event.sso_enabled" type="success" outlined>
                SSO is enabled.
                <v-spacer />
                <v-btn color="success" outlined :to="{name:'eventmanagerIntegrationsSso', params: {id: event.id}}">Configure</v-btn>
              </v-alert>
              
            <v-row>

              <v-col cols="12" class="pb-0">
                <h3>Enterprise Security Settings</h3>
                <p>These settings are available only for Enterprise Plan events.</p>
              </v-col>

              <v-col cols="12" md="12" class="pb-0">
                <ToggleButtonInput v-model="event.datacenter_id" label="Datacenter Location" icon="fa-globe" :items="siteData.datacenters" :disabled="(!eventUtil.isEnterprisePlan()) || event.published" hint="Geopolitical location to store employee information. Enterprise plan only. Cannot be changed after event is published." />
                <a href="https://corporatefitness.helpscoutdocs.com/article/95-choosing-your-datacenter-location" class="font-s ml-8" target="_blank">Read more on choosing a datacenter location</a>
              </v-col>

              <v-col cols="12" md="12" class="pb-0">
                <v-switch v-model="event.enforce_mfa" label="Enforce MFA for admins" prepend-icon="fa-lock" :disabled="!eventUtil.isEnterprisePlan()" persistent-hint hint="Enable to require all (co-)admins of this event to have MFA enabled for enhanced security. Enterprise Plan only." />
                <a href="https://corporatefitness.helpscoutdocs.com/article/107-multi-factor-authentication" class="font-s ml-8" target="_blank">Read more on multi-factor authentication</a>
              </v-col>

              <v-col cols="12" md="12" class="pb-0">
                <v-switch v-model="event.block_geo_data" label="Block collection or processing of participants' geo-location data" prepend-icon="fa-location" :disabled="!eventUtil.isEnterprisePlan()" persistent-hint hint="Block collecting geo-data for enhanced data privacy compliancy. Enterprise Plan only." />
              </v-col>

              <v-col cols="12" md="12" class="pb-0">
                <v-combobox
                  v-model="event.join_valid_domains"
                  :disabled="!eventUtil.isEnterprisePlan()"
                  label="Valid domains (optional)"
                  persistent-hint
                  hint="Enter one or more valid domain names, each followed by the TAB key. When used, only users with an email address from one of the valid domains may join."
                  class="mt-4"
                  prepend-icon="fa-at"
                  multiple
                  chips
                  />
              </v-col>

              <v-col cols="12" class="pb-0">
                <h3>Privacy Settings</h3>
                <p>Control privacy settings of your event.</p>
              </v-col>

              <v-col cols="12" md="6" class="pb-0">
                <v-switch v-model="event.hide_result_details" class="mt-0" label="Hide result details" hint="Enable this to hide result details &amp; breakdowns." persistent-hint></v-switch>
                <v-switch v-model="event.hide_user_data" :disabled="!eventUtil.isEnterprisePlan()" class="mt-0" label="Hide user data for admins" hint="Only specially marked admins will have access to user activity data or reports." persistent-hint></v-switch>
                <v-alert v-if="event.hide_user_data" type="info" outlined>
                  <p>Use this option to ensure your admins will not have access to employee fitness data.</p>
                  <p>Please make sure to add one or more (external) users with the 'User Data' role. Only users with this role will keep access to user data or reports.</p>
                </v-alert>
                <br/>
                <DateWithTimeZonePicker v-model="event.selfdestruct_date" :timeZone="event.timeZoneOlson" clearable :minDate="event.till" label="Data auto-removal date" hint="Optionally, select the date when we should delete all results and user profiles linked to this event." class="mt-4" />
                <v-alert v-if="event.selfdestruct_date" type="info" outlined>
                  <p>We will automatically delete ALL results and user profiles linked to this event at {{ event.selfdestruct_date | localDate('ddd L') }}.</p>
                </v-alert>
                <v-text-field
                  v-model="event.privacy_email"
                  :disabled="!eventUtil.isEnterprisePlan()"
                  label="Privacy Notices Email (optional)"
                  persistent-hint
                  hint="Optionally enter an email address to receive copies of profile removal requests from users belonging to this event"
                  class="mt-4"
                  prepend-icon="fa-bell"
                  multiple
                  chips
                  />
              </v-col>            
            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
        
        <!-- Advanced -->
        <v-expansion-panel v-if="eventUtil.showAdvancedEventOptions()">
          <v-expansion-panel-header>
            <v-icon color="gray" class="shrink mr-4">fadl fal fa-tools</v-icon>
            Advanced Options
          </v-expansion-panel-header>
          <v-expansion-panel-content>
            <p>Advanced options when importing registration data into this event.</p>
            <v-row>
              <v-row class="mx-0">
                <v-col cols="12" class="pb-0">
                  <h3>Registration Process</h3>
                  <p>Use these options to configure a Register Now button, or other main CTA link on the event page.</p>
                </v-col>
                <v-col cols="12" md="4" class="py-0">
                  <v-text-field 
                    v-model="event.reg_url" 
                    class="mt-0 mb-8"
                    label="Registration website" 
                    persistent-hint
                    hint="Direct link to the registration page."
                    />
                </v-col>
                <v-col cols="12" md="4" class="py-0">
                  <v-text-field 
                    v-model="event.reg_text" 
                    class="mt-0 mb-8"
                    label="Button Caption" 
                    persistent-hint
                    placeholder="Register Now"
                    hint="Defaults to 'Register Now'"
                    />
                </v-col>

                <v-col cols="12" md="4" class="py-0">
                  <DateWithTimeZonePicker v-model="event.reg_till" :timeZone="event.timezone" label="Registration Close date (included)" hint="Last day people can still register for this event." />
                </v-col>
              </v-row>
              
              <v-row v-if="tenant.id !== 'cofi'" class="mx-0">
                <v-col cols="12" class="pb-0">
                  <h3>Import file config</h3>
                </v-col>
                <v-col cols="12" md="6" class="py-0">
                  <v-text-field 
                      v-model="event.import_col_code" 
                      label="Column name with verification code" 
                      persistent-hint
                      hint="The column name in the import file containing the verification code. This must be a unique and never-changing code to identify a participant.">
                  </v-text-field>
                </v-col>

                <v-col cols="12" md="6" class="py-0">
                  <v-text-field 
                      v-model="event.import_col_race" 
                      label="Column name with race" 
                      persistent-hint
                      hint="The column name in the import file containing the race name. Each race has a import id field which will be used to lookup the correct race.">
                  </v-text-field>
                  <v-switch 
                    v-model="event.import_ignore_unmapped"
                    label="Ignore records with unmapped leaderboards"
                    persistent-hint
                    hint="Enable this option to filter out all records which cannot be mapped to at least one leaderboard."
                    />

                </v-col>
              </v-row>

              <v-col v-if="event.public" cols="12" md="6" class="py-0">
                <h3>External website</h3>
                <v-text-field 
                    v-model="event.external_url" 
                    label="External website to direct all visitors to until challenge starts" 
                    persistent-hint
                    hint="When this is entered the Event Card will point to this url instead of the challenge, until the challenge starts.">
                </v-text-field>
              </v-col>
              <v-col cols="12" md="6" class="py-0">
                <h3>Result Photo website</h3>
                <v-text-field 
                    v-model="event.result_photo_url" 
                    label="External website with result photos (and videos)" 
                    persistent-hint
                    hint="You may include {bib} and {code} placeholders to point to athlete details pages.">
                </v-text-field>
              </v-col>

              <v-col cols="12" md="6" class="pb-0">
                <h3>Primary Event Type</h3>
                <v-select
                    v-model="event.type" 
                    :items="siteData.event_types"
                    item-text="text"
                    item-value="type"
                    label="Select Event Type" 
                    persistent-hint
                    hint="How can you best describe the type of this event? Skip this question when you're not sure (yet)."
                    />
              </v-col>

              <v-col cols="12" md="6" class="pb-0">
                <v-select
                  v-if="!event.allow_manual_entry"
                  :items="siteData.activities.filter(x => !x.hidden)"
                  v-model="event.manual_entry_activity_types" 
                  multiple
                  item-value="type"
                  :item-text="item => $helpers.getActivityTypeLabel(item.type)"
                  label="Allowed Sports for manual entry"
                  persistent-hint
                  hint="Leave 'All Sports' selected unless you explicitly want to configure this."
                >
                  <template v-slot:prepend-item>
                    <v-list-item 
                      @click="event.manual_entry_activity_types=[]"
                      :class="allActivitiesSelected ? 'v-list-item--active':''"
                    >
                      <v-list-item-action>
                        <v-icon color="primary">{{ event.manual_entry_activity_types == null ? 'fa-check-square' : 'fal fa-square' }}</v-icon>
                      </v-list-item-action>
                      <v-list-item-content>
                        <v-list-item-title>No exceptions</v-list-item-title>
                      </v-list-item-content>
                    </v-list-item>
                    <v-divider class="mt-2"></v-divider>
                  </template>
                </v-select>
              </v-col>

              <v-col cols="12" md="6" class="pb-0">
                <v-switch v-model="event.display_profile_names" class="mt-0" label="Force Display Profile Names" hint="Always show profile names instead of names from import file." persistent-hint></v-switch>
                <v-switch v-model="event.require_proof_manual" class="mt-0" label="Require photo upload when claiming achievement" hint="Require participants to upload a photo/selfie when claiming an achievement." persistent-hint></v-switch>
              </v-col>


              <v-col cols="12" md="6" class="pb-0">
                <h3>Connect with series or organization pages</h3>
                <v-combobox
                  v-model="event.linked_group_ids"
                  :items="groups"
                  item-text="name"
                  item-value="id"
                  label="Connect with organizations or series"
                  prepend-icon="fa-link"
                  :return-object="false"
                  multiple
                  chips
                ></v-combobox>
                <v-switch v-model="event.activity_reuse" class="mt-0" label="Allow activity re-use across series" hint="Enable to allow the same activity to count towards multiple event in this series." persistent-hint></v-switch>
              </v-col>

              <v-col v-if="eventUtil.isProPlan()" cols="12" md="6" class="pb-0">
                <h3>Connect with custom Google Analytics</h3>
                <v-combobox
                  v-model="event.custom_trackers"
                  label="Connect with one or more custom Google Analytic tracking codes"
                  prepend-icon="fa-analytics"
                  multiple
                  chips
                ></v-combobox>
              </v-col>

              <v-col cols="12" md="12" class="pb-0">
                <h3>Event Feed</h3>
                <p>These settings impact the Feed tab in the app.</p>
                <v-switch v-model="event.allow_feed_interactions" class="mt-0" label="Allow users to like and comment on feed items" hint="" persistent-hint></v-switch>
                <v-switch v-model="event.allow_feed_stories" class="mt-0" label="Allow users to post their stories to the feed" hint="" persistent-hint></v-switch>
                <v-switch v-model="event.cross_post_stories" class="mt-0" label="Cross post user stories to Slack (when connected)" hint="" persistent-hint></v-switch>
                <v-switch v-model="event.post_started_feed" class="mt-0" label="Post newly created results to feed (aggregated results only)" hint="This setting can be overwritten on race level." persistent-hint></v-switch>
                <v-switch v-model="event.post_completed_feed" class="mt-0" label="Post completed results to feed" hint="This setting can be overwritten on race level." persistent-hint></v-switch>
                <v-switch v-model="event.allow_photos" class="mt-0" label="Allow photo upload" hint="Enable event and result photo galleries." persistent-hint></v-switch>

                <v-select
                  :items="$helpers.prefixSelectListWith(siteData.boolean_list, '- Use Default -')"
                  v-model="event.allow_team_feed" 
                  item-value="value"
                  item-text="text"
                  label="Enable team feeds (private team chat)"
                  placeholder="- Use Default -"
                  />

                <v-select
                  :items="siteData.content_interactions"
                  v-model="normalizedResultInteractions" 
                  multiple
                  item-value="value"
                  item-text="text"
                  label="Allowed Result Interactions"
                  persistent-hint
                  hint="Specify the allowed interactions by users on results."
                  class="mt-8"
                >
                  
                </v-select>                

              </v-col>

              
              <v-col cols="12" md="12" class="pb-0">
              </v-col>

            </v-row>
          </v-expansion-panel-content>
        </v-expansion-panel>
                
        </v-expansion-panels>
        <InlineTip v-if="eventUtil.hideAdvancedEventOptions()" class="mx-3 mt-4" :to="{name: 'eventmanagerLicense', params: {id:event.id}}">
          Note: some advanced options are not available for this event based on its license and/or support plan. Consider upgrading to a setup-service if you need more customization options for your event.
        </InlineTip>

        <v-card-text>   
          <v-btn class="mt-8 mb-8" block :disabled="!valid" color="primary" :loading="$store.getters.isLoading" @click="submit">Save</v-btn>
          <v-snackbar v-model="savedSnackbar" top color="success" :timeout="6000">
            Hooray, your event is saved!
            <v-btn v-if="event && event.published" text color="white" target="_blank" :to="{name:'event', params: {id: event.id}}">View event</v-btn>
          </v-snackbar>
          <!-- <v-alert v-if="validationError" class="mt-4" type="error">There is at least one validation issue, please check all fields marked in red.</v-alert> -->
          <v-alert v-if="error || !valid" class="mt-4" type="error">{{error || 'There is one or more validation issues. Please check the form fields and correct any errors.' }}</v-alert>
        </v-card-text>   
        </v-form>
      </div>      
    </v-card>

    <v-dialog v-model="showTimezoneDialog" max-width="500">
      <v-card>
        <v-card-title>Modify Event Time Zone</v-card-title>
        <v-card-text>
          <p>Changing the time zone of this event will also convert all dates into this new time zone.</p>
          <v-autocomplete v-model="selectedTimezone" :items="timezoneList" /> 
        </v-card-text>
        <v-card-actions>
          <v-btn text color="primary" @click="showTimezoneDialog=false">Close</v-btn>
          <v-spacer />
          <v-btn color="primary" @click="setTimezone">Update</v-btn>
        </v-card-actions>

      </v-card>
    </v-dialog>
    
    <SupportButton/>
  </div>      
</template>



<script>
import { mapGetters } from "vuex";
import profileService from "@/services/profileService";
import eventManagerService from "@/services/eventManagerService";
import DateWithTimeZonePicker from "@/components/DateWithTimeZonePicker";
import DateAndTimeWithTimeZonePicker from "@/components/DateAndTimeWithTimeZonePicker";
import InlineTip from "@/components/InlineTip";
import DistanceTextArea from "@/components/DistanceTextArea";
import EventImageUploadForm from "@/components/EventImageUploadForm";
import ActivityUploadForm from "@/components/ActivityUploadForm";
import SupportButton from '@/components/SupportButton.vue'
import MarkdownEditor from '@/components/MarkdownEditor.vue'
import EventHeader from "@/components/EventHeader.vue";
import RegFieldEditor from "@/components/RegFieldEditor.vue";
import ToggleButtonInput from "@/components/ToggleButtonInput.vue";
import EventUtil from "@/util/eventUtil";
import { EventBus } from '@/plugins/eventbus.js';
import { DateTime } from 'luxon'
import siteData from '@/data/site.json'
import Header from './_Header.vue'
import tenants from '@/data/tenants.config'
const tenant = tenants.current();

export default {
  name: "Profile",
  components: {
    DateWithTimeZonePicker,
    DateAndTimeWithTimeZonePicker,
    DistanceTextArea,
    EventImageUploadForm,
    RegFieldEditor,
    InlineTip,
    ToggleButtonInput,
    ActivityUploadForm,
    SupportButton,
    MarkdownEditor,
    EventHeader,
    Header,
  },
  props: {
  },
  data() {
    return {
      tenant: tenant,
      siteData: siteData,
      valid: true,
      savedSnackbar: false,
      showColorPickerMenu: false,
      error: null,
      event: null,
      originalEvent: null,
      askForConsent: false,
      askForRaces: false,
      selectedTimezone: null,
      showTimezoneDialog: false,
      timezoneList: [],
      groups: null,
      selectedPanel: null,
      dynamicallyCreateTeams: false,
      nameRules: [
        v => !!v || "Please enter the event name",
      ],
      emailRules: [
        v => !!v || 'E-mail is required',
        v => /.+@.+/.test(v) || 'E-mail must be valid'
      ],
      optionalEmailRules: [
        v => !v || /.+@.+/.test(v) || 'Enter a valid email address'
      ],
      urlRules: [
        v => !v || v.startsWith('https://') || "Website must start with 'https://' (http is not supported!)",
      ],
      numberRules: [
        v => v > 0 || "Please enter a positive number",
      ],
    };
  },
  async mounted() {
    var selectedSection = this.$route.query.section;
    if (selectedSection == 'design') this.selectedPanel = 1;
    if (selectedSection == 'onboarding') this.selectedPanel = 2;
    if (selectedSection == 'rules') this.selectedPanel = 3;

    await this.getProfile();
    
    EventBus.$on('login-state-change', async user => {
      await this.getProfile();
    });
  },
  methods: {
    async submit() {
      if (this.$refs.form.validate()) {
        this.error = null;
        try {
          //var response = await eventManagerService.put(this.event);
          await this.eventUtil.save();

          //await this.getEvent(response.data.id);
          //this.savedSnackbar = true;
          this.$router.push({ name: 'eventmanagerView', params: { id: this.eventUtil.event.id }});
        }
        catch (ex) {
          this.error = ex.response.data.msg;
        }
      }
    },

    async getEvent(id) {
      var response = await eventManagerService.get(id);
      this.event = response.data;
      this.event.timezone = this.event.timezone || DateTime.now().zoneName;
      this.groups = (await profileService.myManagedGroups()).data.data.filter(x => x.type === 'ORGANIZATION' || x.type === 'SERIES');
      this.originalEvent = { ...this.event}; // clone object
      this.askForConsent = this.event.join_consent_msg != null;
      this.askForRaces = this.event.join_select_race_import_ids != null;
      this.dynamicallyCreateTeams = !!this.event.team_name_algo;
    },

    async sendTestEmail() {
      // save first to include textual changes!
      await eventManagerService.put(this.event);
      // now send the email
      const response = await eventManagerService.sendInviteEventEmail(this.event.id);
      this.$helpers.toastResponse(this, response.data, 'The test email is on its way! Please check your inbox in a few minutes from now.');
    },

    async openTimezoneDialog() {
      this.timezoneList = Intl.supportedValuesOf('timeZone');
      this.selectedTimezone = this.event.timezone;
      this.showTimezoneDialog = true;
    },

    async setTimezone() {
      this.event.timezone = this.selectedTimezone;
      this.showTimezoneDialog = false;
    },

    async sendTestBibEmail() {
      // save first to include textual changes!
      await eventManagerService.put(this.event);
      // now send the email
      const response = await eventManagerService.sendBibEmail(this.event.id);
      this.$helpers.toastResponse(this, response.data, 'The test email is on its way! Please check your inbox in a few minutes from now.');
    },

    async sendTestFriendInviteEmail() {
      // save first to include textual changes!
      await eventManagerService.put(this.event);
      // now send the email
      const response = await eventManagerService.sendFriendInviteEmail(this.event.id);
      this.$helpers.toastResponse(this, response.data, 'The test email is on its way! Please check your inbox in a few minutes from now.');
    },

    async getProfile() {
      if (this.user) {
        await this.getEvent(this.$route.params.id);
      }
    },

    checkAllProviders() {
      this.event.allowed_providers = [];
    },
  },
  computed: {
    eventUtil() {
      return new EventUtil(this, this.event);
    },
    betaMode() {
      return process.env.NODE_ENV !== 'production';
    },
    blockDateEdit() {
      return this.event && this.$helpers.hasStarted(this.event);
    },
    breadcrumbItems() {
      return [
        { text: 'All Events', exact: true, to: { name: 'eventmanager' } },
        this.event && this.event.org ? { text: this.event.org.name, exact: true, to: { name: 'orgmanagerView', params: {id: this.event.org.id}} } : null,
        this.event ? { text: this.event.name, exact: true, to: { name: 'eventmanagerView', params: {id: this.event.id}} } : null,
      ].filter(x => x != null);
    },
    showMinMaxRules() {
      return this.event.races && this.event.races.some(x => x.personal_goal);
    },
    normalizedResultInteractions: {
      get () {
        return !this.event || !this.event.allowed_result_interactions ? siteData.content_interactions.map(x => x.value) : this.event.allowed_result_interactions;
      },
      set (value) {
        console.log('/// saving', value);
        this.event.allowed_result_interactions = value.length == siteData.content_interactions.length ? null : value;
        console.log('/// saving', value, this.event.allowed_result_interactions);
      }
    },
    allProvidersSelected() {
      return !this.event.allowed_providers || this.event.allowed_providers.length == 0;
    },
    calculatedTeamMode: {
      get () {
        if (this.event.join_select_team) {
          return "SELF_MANAGED";
        }
        if (this.event.join_random_team_assignment) {
          return "RANDOM";
        }
        if (this.event.admin_assigns_team) {
          return "ADMIN";
        }
        return "NONE";
      },
      set (value) {
        console.log('/// select team mode', value);
        this.event.join_select_team = false;
        this.event.join_random_team_assignment = false;
        this.event.admin_assigns_team = false;
        if (value == "SELF_MANAGED") {
          this.event.join_select_team = true;
        }
        if (value == "RANDOM") {
          this.event.join_random_team_assignment = true;
        }
        if (value == "ADMIN") {
          this.event.admin_assigns_team = true;
        }
        this.$forceUpdate();
      }
    },
    calculatedVerificationMethod:{
      get () {
        return !this.event || !this.event.verification_method ? '_' : this.event.verification_method
      },
      set (value) {
        this.event.verification_method = value === '_' ? null : value;
      }
    },
    computedMinTeamSize:{
      get () {
        return this.event ? this.event.team_min_members : null
      },
      set (value) {
        this.event.team_min_members = value === /*must be triple!*/ 0 ? value : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    computedMaxTeamSize:{
      get () {
        return this.event ? this.event.team_max_members : null
      },
      set (value) {
        this.event.team_max_members = value === /*must be triple!*/ 0 ? value : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    computedMaxDailyStepsSize:{
      get () {
        return this.event ? this.event.daily_steps_max : null
      },
      set (value) {
        this.event.daily_steps_max = value === /*must be triple!*/ 0 ? value : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    computedMaxDailyStepsManualEntry:{
      get () {
        return this.event ? this.event.daily_steps_max_manual : null
      },
      set (value) {
        this.event.daily_steps_max_manual = value === /*must be triple!*/ 0 ? value : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    computedMaxParticipationDuration:{
      get () {
        return this.event ? this.event.max_participation_days : null
      },
      set (value) {
        this.event.max_participation_days = value === /*must be triple!*/ 0 ? null : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    computedAutoLockDays:{
      get () {
        return this.event ? this.event.auto_lock_days : null
      },
      set (value) {
        this.event.auto_lock_days = value === /*must be triple!*/ 0 ? null : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    computedAllowedHistoryDays:{
      get () {
        return this.event ? this.event.allowed_history_days : null
      },
      set (value) {
        this.event.allowed_history_days = value === /*must be triple!*/ 0 ? value : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    computedAllowedBackLoadingDays:{
      get () {
        return this.event ? this.event.allowed_backloading_days : null
      },
      set (value) {
        this.event.allowed_backloading_days = value === /*must be triple!*/ 0 ? value : value || null // coerce to null (except for zero!) (empty string is otherwise an issue)
        this.$forceUpdate();
      }
    },
    colorForBinding: {
      get() {
        console.log('GET', this.event.color);
        if (!this.event.color || this.event.color.length == 0) {
          return "#FFFFFF";
        }
        return this.event.color;
      },
      set(value) {
        console.log('SET', value);
        if (value && value.length > 7) {
          value = value.substring(0, 7).toUpperCase();
          if (value === '#FFFFFF') {
            value = null;
          }
        }
        this.event.color = value;
        this.$forceUpdate();
      }
    },
    swatchStyle() {
      const { event, showColorPickerMenu } = this
      return {
        backgroundColor: event.color,
        cursor: 'pointer',
        height: '30px',
        width: '30px',
        border: 'solid 1px #ccc',
        borderRadius: showColorPickerMenu ? '50%' : '4px',
        transition: 'border-radius 200ms ease-in-out'
      }
    },
    ...mapGetters({
      user: "user"
    })
  },
  watch: {
  },
};
</script>
<style lang="scss">
</style>

